import useIdentifierAssociations from '@cobliteam/hooks/src/useEntity/useIdentifierAssociations';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import useIsOnboardingFleet from '@cobliteam/hooks/src/useIsOnboardingFleet/useIsOnboardingFleet';
import { useLanguage } from '@cobliteam/i18n';
import { usePermissions } from '@cobliteam/permissions';
import type React from 'react';
import { createContext, useContext } from 'react';
import type { Environment, RouteDef, RuntimeFlags, SidebarDef } from '../dashboardDef';
import dashboardDefFile from '../dashboardDef.json';
import InitialLoadingPage from '../pages/LoadingPage/InitialLoadingPage';
import getEnvironment from './DashboardProvider/helpers/getEnvironment';
import getFleetSize from './DashboardProvider/helpers/getFleetSize';
import getInactiveFleetFallbackUserData from './DashboardProvider/helpers/getInactiveFleetFallbackUserData';
import getSidebar from './DashboardProvider/helpers/getSidebar';

// Define what the context provides
interface DashboardContextValue {
  dashboardDef: RouteDef[];
  sidebar: SidebarDef;
  runtimeFlags: RuntimeFlags;
  environment: Environment;
}

// Create the context
const DashboardContext = createContext<DashboardContextValue | undefined>(undefined);

// Provider props
interface SimpleProviderProps {
  children: React.ReactNode;
}

// Provider component
const DashboardProvider = ({ children }: SimpleProviderProps) => {
  const { isAccessAllowed } = usePermissions();
  const { currentLanguage } = useLanguage();
  const featureFlags = useFeatureFlags();
  const { session, user, isSafetyCoacher, hasNoValidGroups, hasOnlyGroupsWithNoVehicles, noValidGroupOrVehicles, hasGroupsRestriction, useGetUserData } = useUser();
  const { data: userData } = useGetUserData(session?.user.id, !!session);
  // Frota inativa: o boot não espera (nem depende do) getUserData — sem basic:permission os
  // resolvers do janus falham no bouncer. Um userData mínimo derivado da própria sessão destrava
  // a página de boletos na hora; se a query resolver depois, o dado real substitui o fallback.
  const effectiveUserData = userData ?? (session?.fleet.active === false ? getInactiveFleetFallbackUserData(session) : undefined);
  const fleetSize = getFleetSize(effectiveUserData);
  const dashboardDef = dashboardDefFile as RouteDef[];

  // Must be called before any conditional return to comply with React hooks rules
  const { data: hasIdentifiers } = useIdentifierAssociations.useHasIdentificationTokens();
  const { isOnboardingFleet } = useIsOnboardingFleet();

  if (!(user && effectiveUserData)) return <InitialLoadingPage />;

  // Flags that are used at runtime to build the sidebar
  const runtimeFlags: RuntimeFlags = {
    hasCameraFlag: featureFlags.enforceCameraFeatures || effectiveUserData?.hasCameraFlag,
    hasRedeCan: effectiveUserData?.hasRedeCan,
    hasGroupsRestriction,
    hasNoValidGroups,
    hasOnlyGroupsWithNoVehicles,
    noValidGroupOrVehicles,
    fleetSize,
    isSafetyCoacher,
    // Default true while loading — hide only once confirmed empty
    hasIdentifiers: hasIdentifiers ?? true,
    // Esconde o "Guia de configuração" até o status confirmar onboarding (default seguro, sem flicker
    // para clientes estabelecidos); erro/timeout também caem em escondido.
    isOnboardingFleet,
    // Frota inativa loga (o severino devolve a frota quando o usuário não tem nenhuma ativa)
    // mas só a página de boletos fica acessível; `active` ausente conta como ativa
    isInactiveFleet: session?.fleet.active === false,
  };

  const environment = getEnvironment();

  const sidebar = getSidebar({ dashboardDef, runtimeFlags, featureFlags, isAccessAllowed, language: currentLanguage });

  const initialValues = { dashboardDef, sidebar, runtimeFlags, environment };

  return <DashboardContext.Provider value={initialValues}>{children}</DashboardContext.Provider>;
};

// Custom hook
export const useDashboard = () => {
  const context = useContext(DashboardContext);

  if (!context) throw new Error('useDashboard must be used within DashboardProvider');

  return context;
};

export default DashboardProvider;
